import {
  getCookie,
  setCookie,
  deleteCookie,
  getAllCookies,
  type CookieOptions,
} from "@repo/client-cookies";

export const BAG_COOKIE_NAMES = {
  SESSION_TOKEN: "asos-bag-session-token",
  BAG_ID_PREFIX: "BagId:",
  BAG_ORIGIN_PREFIX: "BagOrigin:",
} as const;

export type BagCookieConfig = {
  store: string;
  country: string;
};

const getCookieDomain = () => {
  if (typeof window === "undefined" || !window.location) {
    return undefined;
  }

  return (
    window.location.hostname.split(".").splice(1, 2).join(".") || undefined
  );
};

const getCookieOptions = (): CookieOptions => {
  const domain = getCookieDomain();

  return {
    path: "/",
    domain,
    sameSite: "Strict",
  };
};

export const setBagCookies = (
  config: BagCookieConfig,
  {
    bagId,
    bagOrigin,
    sessionToken,
  }: {
    bagId?: string;
    bagOrigin?: string;
    sessionToken?: string;
  },
) => {
  const cookieOptions = getCookieOptions();

  if (bagId) {
    const bagIdCookieName = `${BAG_COOKIE_NAMES.BAG_ID_PREFIX}${config.store}:${config.country}`;
    setCookie(bagIdCookieName, bagId, {
      ...cookieOptions,
      expiresInMinutes: 60,
    });
  }

  if (bagOrigin) {
    const bagOriginCookieName = `${BAG_COOKIE_NAMES.BAG_ORIGIN_PREFIX}${bagOrigin}`;
    setCookie(bagOriginCookieName, bagOrigin, {
      ...cookieOptions,
      expiresInMinutes: 60,
    });
  }

  if (sessionToken) {
    setCookie(BAG_COOKIE_NAMES.SESSION_TOKEN, sessionToken, {
      ...cookieOptions,
    });
  }
};

export const getBagCookieValues = (config: BagCookieConfig) => {
  const allCookies = getAllCookies();

  const bagIdCookieName = `${BAG_COOKIE_NAMES.BAG_ID_PREFIX}${config.store}:${config.country}`;
  const bagIdCookie = getCookie(bagIdCookieName);
  const bagOriginCookie = allCookies.find((cookie) =>
    cookie.name.startsWith(BAG_COOKIE_NAMES.BAG_ORIGIN_PREFIX),
  );
  const sessionTokenCookie = getCookie(BAG_COOKIE_NAMES.SESSION_TOKEN);

  return {
    bagId: bagIdCookie || "",
    bagOrigin: bagOriginCookie?.value || "",
    sessionToken: sessionTokenCookie || "",
  };
};

export const deleteAllBagCookies = (config: BagCookieConfig) => {
  const domain = getCookieDomain();
  const cookieDeleteOptions = {
    path: "/",
    domain,
  };

  const bagIdCookieName = `${BAG_COOKIE_NAMES.BAG_ID_PREFIX}${config.store}:${config.country}`;
  deleteCookie(bagIdCookieName, cookieDeleteOptions);

  const allCookies = getAllCookies();
  allCookies.forEach((cookie) => {
    if (cookie.name.startsWith(BAG_COOKIE_NAMES.BAG_ORIGIN_PREFIX)) {
      deleteCookie(cookie.name, cookieDeleteOptions);
    }
  });

  deleteCookie(BAG_COOKIE_NAMES.SESSION_TOKEN, cookieDeleteOptions);
};
